// Push Notification Service for TradeRelax using Firebase Cloud Messaging
import { messaging, getToken, onMessage } from '@/config/firebase';

const API = process.env.REACT_APP_BACKEND_URL;

// VAPID Key for web push (you'll need to generate this in Firebase Console)
// Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

class NotificationService {
  constructor() {
    this.swRegistration = null;
    this.fcmToken = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // Initialize service worker
  async init() {
    if (!this.isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.swRegistration);
      
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Service Worker ready');
      
      // Setup Firebase message listener for foreground messages
      if (messaging) {
        onMessage(messaging, (payload) => {
          console.log('Foreground message received:', payload);
          this.showLocalNotification(
            payload.notification?.title || 'TradeRelax Alert',
            {
              body: payload.notification?.body || '',
              icon: '/icons/icon-192x192.png',
              data: payload.data
            }
          );
        });
      }
      
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  // Check if notifications are supported
  isNotificationSupported() {
    return this.isSupported;
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isNotificationSupported()) return 'unsupported';
    return Notification.permission; // 'granted', 'denied', 'default'
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isNotificationSupported()) {
      return { success: false, reason: 'unsupported' };
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        return { success: true, permission };
      } else {
        return { success: false, permission, reason: 'denied' };
      }
    } catch (error) {
      console.error('Permission request failed:', error);
      return { success: false, reason: error.message };
    }
  }

  // Get FCM token
  async getFCMToken() {
    if (!messaging || !this.swRegistration) {
      console.log('Messaging or SW not available');
      return null;
    }

    try {
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: this.swRegistration
      });
      
      if (token) {
        console.log('FCM Token obtained:', token.substring(0, 20) + '...');
        this.fcmToken = token;
        return token;
      } else {
        console.log('No FCM token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  // Subscribe to push notifications
  async subscribe(authToken) {
    if (!this.swRegistration) {
      await this.init();
    }

    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    try {
      // Get FCM token
      const fcmToken = await this.getFCMToken();
      
      if (fcmToken) {
        // Send FCM token to backend
        await this.saveSubscription(fcmToken, authToken);
        console.log('Push subscription saved with FCM token');
        return fcmToken;
      }
      
      // Fallback to native web push if FCM fails
      let subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (!subscription) {
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          // Using a placeholder VAPID key - will work for local notifications
          applicationServerKey: this.urlBase64ToUint8Array(
            'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
          )
        });
      }

      if (subscription) {
        await this.saveSubscription(subscription.toJSON(), authToken);
        console.log('Native push subscription saved');
        return subscription;
      }
      
      return null;
    } catch (error) {
      console.error('Subscription failed:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(authToken) {
    if (!this.swRegistration) return;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      await this.removeSubscription(authToken);
      this.fcmToken = null;
      console.log('Unsubscribed from push notifications');
    } catch (error) {
      console.error('Unsubscribe failed:', error);
    }
  }

  // Save subscription to backend
  async saveSubscription(subscriptionData, authToken) {
    try {
      const response = await fetch(`${API}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          subscription: subscriptionData,
          fcm_token: typeof subscriptionData === 'string' ? subscriptionData : null
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to save subscription:', error);
      throw error;
    }
  }

  // Remove subscription from backend
  async removeSubscription(authToken) {
    try {
      await fetch(`${API}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });
    } catch (error) {
      console.error('Failed to remove subscription:', error);
    }
  }

  // Show local notification (for testing and foreground)
  async showLocalNotification(title, options = {}) {
    if (!this.swRegistration) {
      await this.init();
    }

    if (Notification.permission !== 'granted') {
      console.log('Notification permission not granted');
      return;
    }

    const defaultOptions = {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      vibrate: [200, 100, 200],
      tag: 'traderelax-local',
      requireInteraction: false,
      ...options
    };

    if (this.swRegistration) {
      await this.swRegistration.showNotification(title, defaultOptions);
    } else {
      new Notification(title, defaultOptions);
    }
  }

  // Helper: Convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;

// Export utility functions
export const initNotifications = () => notificationService.init();
export const requestNotificationPermission = () => notificationService.requestPermission();
export const subscribeToNotifications = (token) => notificationService.subscribe(token);
export const unsubscribeFromNotifications = (token) => notificationService.unsubscribe(token);
export const showNotification = (title, options) => notificationService.showLocalNotification(title, options);
export const getNotificationPermission = () => notificationService.getPermissionStatus();
export const isNotificationSupported = () => notificationService.isNotificationSupported();
